import { Circle, Ellipse, Group, Line, Rect } from "react-konva";
import type {
  CircleObject,
  DimObject,
  FormationObject,
  SpotlightObject,
} from "@/lib/canvas/telestrator-schema";
import { LEGACY_DEFAULT_COLOR } from "@/lib/canvas/shared-shape";
import { pointToPx, type Dims } from "@/lib/canvas/coords";

/**
 * Renderers for the three telestrator-only overlay kinds: spotlight,
 * dim, and formation. Imported by `TelestratorCanvas.tsx`. Each
 * renderer mirrors the legacy painter's behavior at
 * `js/coaching/review.js:1283-1359`.
 *
 * The spotlight is the most interesting case — it draws a full-frame
 * darkening overlay with a circular hole punched at the spotlight's
 * center via `globalCompositeOperation: "destination-out"`. Konva
 * doesn't have a declarative spotlight primitive, so this uses a
 * custom `sceneFunc` on a `Group`: fill the rect, switch composite
 * mode, draw the ellipse hole, switch composite mode back. The same
 * trick mirrors how Konva docs recommend implementing custom
 * compositing on a single layer without `cache()` overhead.
 */

const SELECTED_COLOR = "#fbbf24";

export type SpotlightRendererProps = {
  shape: SpotlightObject;
  dims: Dims;
  selected?: boolean;
  onSelect?: () => void;
  draggable?: boolean;
  onDragEnd?: (shape: SpotlightObject) => void;
};

/** Spotlight — full-frame darkening overlay with a circular hole at
 * (x+w/2, y+h/2).
 *
 * Render strategy: three Konva primitives composited on a single
 * Group. The earlier attempt used a `Group.sceneFunc` (broken —
 * Group only draws children) and then `<Shape>.sceneFunc` (also
 * broken inside react-konva: raw 2D paint + globalCompositeOperation
 * inside a sceneFunc doesn't reliably surface in the Layer's
 * scene canvas).
 *
 * Konva DOES support `globalCompositeOperation` as a first-class
 * prop on every Node — it's applied via `setCompositeOperation`
 * before the Layer draws that node. Use that primitive instead of
 * raw canvas ops:
 *   1. A `<Rect>` at 48% black filling the entire frame.
 *   2. A `<Circle>` with `globalCompositeOperation="destination-out"`
 *      to erase the alpha of (1) inside the hole region. Konva
 *      applies the composite op around just this node's draw call,
 *      so the rest of the layer is untouched.
 *   3. A `<Circle>` stroked with the spotlight color for the ring.
 *
 * Hit-detection: a sibling transparent `<Rect>` over the full frame
 * catches clicks anywhere inside the spotlight bounds. PR #6 fix.
 */
export function SpotlightRenderer(props: SpotlightRendererProps) {
  const { shape, dims, selected, onSelect, draggable, onDragEnd } = props;
  const color = selected ? SELECTED_COLOR : shape.color ?? LEGACY_DEFAULT_COLOR;
  const cx = (shape.x + shape.w / 2) * dims.width;
  const cy = (shape.y + shape.h / 2) * dims.height;
  const halfPxW = (shape.w / 2) * dims.width;
  const halfPxH = (shape.h / 2) * dims.height;
  const holeRadius = Math.max(8, Math.min(halfPxW, halfPxH));
  const ringStrokeWidth = shape.strokeWidth ?? 3;

  return (
    <Group
      id={shape.id}
      onClick={onSelect}
      onTap={onSelect}
      draggable={draggable}
      onDragEnd={(e) => {
        if (!onDragEnd) return;
        const dx = e.target.x() / dims.width;
        const dy = e.target.y() / dims.height;
        e.target.position({ x: 0, y: 0 });
        onDragEnd({
          ...shape,
          x: clamp01(shape.x + dx),
          y: clamp01(shape.y + dy),
        });
      }}
    >
      {/* 1. Full-frame darkening at 48% black. */}
      <Rect
        x={0}
        y={0}
        width={dims.width}
        height={dims.height}
        fill="rgba(0, 0, 0, 0.48)"
        listening={false}
      />
      {/* 2. Hole punch — a Circle with destination-out composite
       * mode. Konva applies the composite op around just this
       * draw, so the underlying darkening Rect gets its alpha
       * erased inside the hole circle. Surrounding Layer content
       * is untouched. */}
      <Circle
        x={cx}
        y={cy}
        radius={holeRadius}
        fill="rgba(0, 0, 0, 1)"
        globalCompositeOperation="destination-out"
        listening={false}
      />
      {/* 3. Spotlight ring on top so the user sees the bounds.
       * Source-over (default) lays the stroke on top of the
       * cleared hole. */}
      <Circle
        x={cx}
        y={cy}
        radius={holeRadius}
        stroke={color}
        strokeWidth={ringStrokeWidth}
        fillEnabled={false}
        listening={false}
      />
      {/* Invisible hit-detection rect. Catches clicks anywhere on
       * the full frame so a user can select/drag the spotlight by
       * clicking either the ring OR the darkening fill. `opacity: 0`
       * keeps it invisible; `listening: true` keeps it interactive. */}
      <Rect
        x={0}
        y={0}
        width={dims.width}
        height={dims.height}
        opacity={0}
        listening
      />
    </Group>
  );
}

export type CircleRendererProps = {
  shape: CircleObject;
  dims: Dims;
  selected?: boolean;
  onSelect?: () => void;
  draggable?: boolean;
  onDragEnd?: (shape: CircleObject) => void;
};

/** Circle — a stroked ellipse inscribed in the `(x, y, w, h)`
 * bounds. Ports legacy `js/coaching/review.js:1327-1329` (the
 * non-spotlight branch of the shared circle/spotlight render path):
 * an `ctx.ellipse` stroked at the bounds' center with half-width /
 * half-height radii. Unlike the spotlight there's no darkening
 * overlay — just the ring. Selection bumps the stroke + recolors to
 * the shared selected-amber so it stays obvious.
 *
 * Hit-detection: an invisible `<Rect>` over the bounds catches
 * clicks anywhere inside (same PR #6 fix the spotlight uses) so a
 * thin ring is still easy to grab. */
export function CircleRenderer(props: CircleRendererProps) {
  const { shape, dims, selected, onSelect, draggable, onDragEnd } = props;
  const color = selected ? SELECTED_COLOR : shape.color ?? LEGACY_DEFAULT_COLOR;
  const cx = (shape.x + shape.w / 2) * dims.width;
  const cy = (shape.y + shape.h / 2) * dims.height;
  const radiusX = Math.max(4, (shape.w / 2) * dims.width);
  const radiusY = Math.max(4, (shape.h / 2) * dims.height);
  const strokeWidth = (shape.strokeWidth ?? 3) + (selected ? 1.5 : 0);

  return (
    <Group
      id={shape.id}
      onClick={onSelect}
      onTap={onSelect}
      draggable={draggable}
      onDragEnd={(e) => {
        if (!onDragEnd) return;
        const dx = e.target.x() / dims.width;
        const dy = e.target.y() / dims.height;
        e.target.position({ x: 0, y: 0 });
        onDragEnd({
          ...shape,
          x: clamp01(shape.x + dx),
          y: clamp01(shape.y + dy),
        });
      }}
    >
      <Ellipse
        x={cx}
        y={cy}
        radiusX={radiusX}
        radiusY={radiusY}
        stroke={color}
        strokeWidth={strokeWidth}
        fillEnabled={false}
        listening={false}
      />
      {/* Invisible hit-detection rect over the bounds — same pattern
       * as the spotlight so a thin ring is still grabbable. */}
      <Rect
        x={shape.x * dims.width}
        y={shape.y * dims.height}
        width={shape.w * dims.width}
        height={shape.h * dims.height}
        opacity={0}
        listening
      />
    </Group>
  );
}

export type DimRendererProps = {
  shape: DimObject;
  dims: Dims;
  selected?: boolean;
  onSelect?: () => void;
};

/** Dim — full-frame translucent black overlay. The simplest of the
 * three. Selection state shows a thin amber ring around the frame so
 * the user can find it to delete it. */
export function DimRenderer(props: DimRendererProps) {
  const { shape, dims, selected, onSelect } = props;
  return (
    <Group id={shape.id} onClick={onSelect} onTap={onSelect}>
      <Rect
        x={0}
        y={0}
        width={dims.width}
        height={dims.height}
        fill={`rgba(0, 0, 0, ${shape.opacity})`}
      />
      {selected ? (
        <Rect
          x={2}
          y={2}
          width={dims.width - 4}
          height={dims.height - 4}
          stroke={SELECTED_COLOR}
          strokeWidth={3}
          dash={[6, 4]}
          listening={false}
        />
      ) : null}
    </Group>
  );
}

export type FormationRendererProps = {
  shape: FormationObject;
  dims: Dims;
  selected?: boolean;
  onSelect?: () => void;
};

/** Formation — anchors rendered as filled circles + the convex hull
 * as a closed polygon connecting them. The hull is precomputed at
 * save time (via `convexHull` in telestrator-schema.ts); render-time
 * just draws what's there. */
export function FormationRenderer(props: FormationRendererProps) {
  const { shape, dims, selected, onSelect } = props;
  const color = selected ? SELECTED_COLOR : shape.color ?? LEGACY_DEFAULT_COLOR;
  const strokeWidth = (shape.strokeWidth ?? 2) + (selected ? 1.5 : 0);

  const hullPoints: number[] = [];
  for (const p of shape.hull_points) {
    const px = pointToPx(p, dims);
    hullPoints.push(px.x, px.y);
  }

  return (
    <Group id={shape.id} onClick={onSelect} onTap={onSelect}>
      {shape.hull_points.length >= 3 ? (
        <Line
          points={hullPoints}
          closed
          stroke={color}
          strokeWidth={strokeWidth}
          fill={`${hexToRgba(color, 0.16)}`}
          dash={[8, 5]}
        />
      ) : null}
      {shape.anchors.map((a, i) => {
        const center = pointToPx(a, dims);
        return (
          <Group key={`${a.player_id ?? "anchor"}-${i}`} x={center.x} y={center.y}>
            <Circle radius={10} fill={color} stroke="rgba(15, 23, 42, 0.6)" strokeWidth={1.5} />
            {a.label ? (
              <Line
                points={[]}
                listening={false}
                // Label text rendered separately to keep this overlay
                // light; a future revision can add a centered <Text>
                // here. The legacy painter renders the jersey number
                // inline; for M3 we keep formations minimal so the
                // demo can ship.
              />
            ) : null}
          </Group>
        );
      })}
    </Group>
  );
}

function hexToRgba(hex: string, alpha: number): string {
  if (!/^#[0-9a-fA-F]{6}$/.test(hex)) return `rgba(148, 163, 184, ${alpha})`;
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function clamp01(v: number): number {
  if (!Number.isFinite(v)) return 0;
  if (v < 0) return 0;
  if (v > 1) return 1;
  return v;
}
